import { z } from 'zod';
import {
  DraftStatusSchema,
  ProcedureIdSchema,
  SignatureDataUrlSchema,
  UserIdSchema,
} from './common';

/** Clasificación ASA del estado físico del paciente. */
export const AsaClassSchema = z.enum(['I', 'II', 'III', 'IV', 'V', 'E']);
export type AsaClass = z.infer<typeof AsaClassSchema>;

/** Escala de Mallampati para vía aérea. */
export const MallampatiSchema = z.enum(['I', 'II', 'III', 'IV']);
export type Mallampati = z.infer<typeof MallampatiSchema>;

/**
 * Signos vitales — solo captura **inicial** (no monitoreo en tiempo real).
 * Confirmado con el usuario: vitals.js representa una sola toma en pre-anestésica.
 */
export const VitalSignsInitialSchema = z.object({
  tensionSistolica: z.number().int().min(40).max(260),
  tensionDiastolica: z.number().int().min(20).max(160),
  frecuenciaCardiaca: z.number().int().min(20).max(220),
  saturacionO2: z.number().int().min(50).max(100),
  frecuenciaRespiratoria: z.number().int().min(4).max(60).optional(),
  temperatura: z.number().min(28).max(43).optional(),
});
export type VitalSignsInitial = z.infer<typeof VitalSignsInitialSchema>;

export const PreAnesthesiaSchema = z.object({
  pesoKg: z.number().min(0.5).max(400),
  tallaCm: z.number().int().min(20).max(250),
  asa: AsaClassSchema,
  mallampati: MallampatiSchema.optional(),
  ayunoHoras: z.number().int().min(0).max(72),
  antecedentes: z.string().max(2000).optional(),
  vitalesIniciales: VitalSignsInitialSchema,
});
export type PreAnesthesia = z.infer<typeof PreAnesthesiaSchema>;

/** Técnica anestésica. */
export const AnesthesiaTechniqueSchema = z.enum([
  'sedacion',
  'sedacion-profunda',
  'general',
  'regional',
  'local',
  'combinada',
]);
export type AnesthesiaTechnique = z.infer<typeof AnesthesiaTechniqueSchema>;

export const AnesthesiaMedicationSchema = z.object({
  codigo: z.string().min(2).max(20),
  nombre: z.string().min(2).max(150),
  dosis: z.string().min(1).max(60),
  via: z.enum(['IV', 'IM', 'inhalatoria', 'oral', 'topica', 'epidural', 'subaracnoidea']),
  hora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
export type AnesthesiaMedication = z.infer<typeof AnesthesiaMedicationSchema>;

export const AnesthesiaRecordSchema = z.object({
  procedimientoId: ProcedureIdSchema,
  anestesiologoId: UserIdSchema,
  // Política: el anestesiólogo llena lo que considere relevante. Solo es
  // obligatoria la firma (y los IDs de procedimiento + anestesiólogo).
  // Todo el contenido clínico estructurado es opcional para reflejar el
  // formato impreso "Registro de Anestesia" de Endomedical.
  preAnestesia: PreAnesthesiaSchema.optional(),
  tecnica: AnesthesiaTechniqueSchema.optional(),
  medicamentos: z.array(AnesthesiaMedicationSchema).max(50).optional(),
  inicioHora: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  finHora: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  complicaciones: z.string().max(2000).optional(),
  observaciones: z.string().max(2000).optional(),
  /**
   * Mapa libre de campos del formulario (identificación, valoración
   * anestésica, monitoreo, balance de líquidos, etc.). La UI define las
   * claves; el backend solo persiste y devuelve el mapa íntegro como JSON.
   */
  freeFormData: z.record(z.string(), z.unknown()).optional(),
  /**
   * PNG dataURL del gráfico de signos vitales dibujado a mano alzada por
   * el anestesiólogo sobre la grilla 0-240 min × valores. Opcional.
   */
  graficaVitalesUrl: SignatureDataUrlSchema.optional(),
  /** `borrador` (preguardado) o `firmado` (final). */
  estado: DraftStatusSchema,
  /** Nula mientras es borrador; presente al firmar. */
  firmaAnestesiologo: SignatureDataUrlSchema.nullable(),
  /** Nula mientras es borrador; timestamp del servidor al firmar. */
  firmadoEn: z.string().datetime().nullable(),
});
export type AnesthesiaRecord = z.infer<typeof AnesthesiaRecordSchema>;

/** Firma (final): la firma del anestesiólogo es OBLIGATORIA. */
export const SignAnesthesiaSchema = AnesthesiaRecordSchema.omit({
  estado: true,
  firmadoEn: true,
}).extend({
  firmaAnestesiologo: SignatureDataUrlSchema,
});
export type SignAnesthesiaInput = z.infer<typeof SignAnesthesiaSchema>;

/** Preguardado (borrador): mismo contenido pero SIN exigir la firma. */
export const SaveAnesthesiaDraftSchema = AnesthesiaRecordSchema.omit({
  estado: true,
  firmadoEn: true,
  firmaAnestesiologo: true,
});
export type SaveAnesthesiaDraftInput = z.infer<typeof SaveAnesthesiaDraftSchema>;
